import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

let ffmpegInstance: FFmpeg | null = null;
let isLoaded = false;

export const loadFFmpeg = async (): Promise<FFmpeg> => {
  if (ffmpegInstance && isLoaded) {
    return ffmpegInstance;
  }

  const ffmpeg = new FFmpeg();
  const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm';

  try {
    // Charger FFmpeg (peut prendre quelques secondes la première fois)
    await ffmpeg.load({
      coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
    });
    ffmpegInstance = ffmpeg;
    isLoaded = true;
    return ffmpeg;
  } catch (error) {
    console.error('Failed to load FFmpeg:', error);
    throw new Error('Impossible de charger FFmpeg. Vérifiez votre connexion internet.');
  }
};

export const canPlayVideo = (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    let resolved = false;
    
    const cleanup = () => {
      if (!resolved) {
        resolved = true;
        URL.revokeObjectURL(url);
      }
    };
    
    const handleSuccess = () => {
      if (!resolved) {
        resolved = true;
        URL.revokeObjectURL(url);
        resolve(true);
      }
    };
    
    const handleError = () => {
      if (!resolved) {
        resolved = true;
        URL.revokeObjectURL(url);
        resolve(false);
      }
    };
    
    video.oncanplay = handleSuccess;
    video.oncanplaythrough = handleSuccess;
    video.onerror = handleError;
    video.onloadeddata = () => {
      // Vérifier si la vidéo a vraiment des données
      if (video.readyState >= 2) {
        handleSuccess();
      }
    };
    
    video.preload = 'auto';
    video.src = url;
    video.load();
    
    // Timeout après 5 secondes
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        URL.revokeObjectURL(url);
        resolve(false);
      }
    }, 5000);
  });
};

export const transcodeVideo = async (
  file: File,
  onProgress?: (progress: number) => void
): Promise<Blob> => {
  const ffmpeg = await loadFFmpeg();
  
  const inputFileName = 'input.' + file.name.split('.').pop();
  const outputFileName = 'output.mp4';
  
  let duration = 0;
  let lastProgress = 0;
  
  // Écouter les logs pour le progrès
  ffmpeg.on('log', ({ message }) => {
    // Extraire la durée totale
    const durationMatch = message.match(/Duration: (\d{2}):(\d{2}):(\d{2}\.\d{2})/);
    if (durationMatch && duration === 0) {
      const hours = parseInt(durationMatch[1]);
      const minutes = parseInt(durationMatch[2]);
      const seconds = parseFloat(durationMatch[3]);
      duration = hours * 3600 + minutes * 60 + seconds;
    }
    
    // Extraire le temps actuel
    const timeMatch = message.match(/time=(\d{2}):(\d{2}):(\d{2}\.\d{2})/);
    if (timeMatch && duration > 0 && onProgress) {
      const hours = parseInt(timeMatch[1]);
      const minutes = parseInt(timeMatch[2]);
      const seconds = parseFloat(timeMatch[3]);
      const currentTime = hours * 3600 + minutes * 60 + seconds;
      const progress = Math.min(95, (currentTime / duration) * 100);
      
      // Ne mettre à jour que si le progrès a significativement changé
      if (Math.abs(progress - lastProgress) > 1) {
        lastProgress = progress;
        onProgress(progress);
      }
    }
  });
  
  // Écrire le fichier d'entrée
  if (onProgress) onProgress(5);
  await ffmpeg.writeFile(inputFileName, await fetchFile(file));
  
  if (onProgress) onProgress(10);
  
  // Transcoder en MP4 avec H.264 (codec largement supporté)
  await ffmpeg.exec([
    '-i', inputFileName,
    '-c:v', 'libx264',        // Codec vidéo H.264
    '-preset', 'fast',        // Preset rapide
    '-crf', '23',             // Qualité (23 = bonne qualité)
    '-c:a', 'aac',            // Codec audio AAC
    '-b:a', '128k',           // Bitrate audio
    '-movflags', '+faststart', // Optimisation pour streaming web
    '-progress', 'pipe:1',    // Afficher le progrès
    '-y',                     // Écraser le fichier de sortie
    outputFileName
  ]);
  
  if (onProgress) onProgress(95);
  
  // Lire le fichier de sortie
  const data = await ffmpeg.readFile(outputFileName);
  
  // Nettoyer
  await ffmpeg.deleteFile(inputFileName);
  await ffmpeg.deleteFile(outputFileName);
  
  if (onProgress) onProgress(100);
  
  return new Blob([data], { type: 'video/mp4' });
};

